import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { isPortalEnabled } from '@/lib/portal/constants';
import { isQuietShellRoute } from '@/lib/routing/quiet-shell';
import { useModalStore } from '@/lib/stores/modal-store';
import { logger } from '@/lib/utils/logger';
import { AnimatePresence,motion } from 'framer-motion';
import { Loader2,Menu,Shield,User,X } from 'lucide-react';
import React,{ useCallback,useEffect,useState } from 'react';
import { Link,useLocation,useNavigate } from 'react-router-dom';
import { AuthModal } from './auth/AuthModal';
import { Button } from './ui/Button';

// Memoized AuthModal to prevent recreation when user state changes
const MemoizedAuthModal = React.memo(function MemoizedAuthModal() {
  const { authModal, closeAuthModal } = useModalStore();
  
  return (
    <AuthModal 
      isOpen={authModal.isOpen}
      onClose={closeAuthModal}
      defaultTab={authModal.tab}
    />
  );
});

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut, isLoading } = useUnifiedAuth();
  const { openAuthModal } = useModalStore();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Close mobile menu on route change so quiet-shell early-return does not
  // leave isOpen true when returning to a marketing route.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleAuth = useCallback((type: 'sign-in' | 'sign-up') => {
    openAuthModal(type);
  }, [openAuthModal]);

  // REN-157: flag ON labels the entry "Portal" and goes straight to /portal
  // (no silent /profile→/portal redirect hop); flag OFF keeps legacy Profile.
  const memberHomePath = isPortalEnabled() ? '/portal' : '/profile';
  const memberHomeLabel = isPortalEnabled() ? 'Portal' : 'Profile';

  const handleMemberHome = useCallback(() => {
    navigate(memberHomePath);
    setIsOpen(false);
  }, [navigate, memberHomePath]);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return; // Prevent multiple clicks
    
    try {
      setIsSigningOut(true);
      logger.info('Sign out button clicked');
      await signOut();
      logger.info('Sign out successful');
      
      // Force navigation on mobile devices
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        window.location.href = '/';
      } else {
        navigate('/');
      }
      
      setIsOpen(false);
    } catch (error) {
      logger.error('Sign out error', { error });
    } finally {
      setIsSigningOut(false);
    }
  }, [signOut, navigate, isSigningOut]);

  const handleMenuToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const userRole = user?.user_metadata?.role || profile?.role;
  const isAdmin = userRole === 'admin';
  const showAdminButton = user && (isAdmin || (isLoading && user.email?.includes('@letsrendezvous.co')));

  if (isQuietShellRoute(location.pathname)) {
    return null;
  }

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-500 ease-in-out ${
        isScrolled ? 'bg-surface-primary/90 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="font-brand-display text-2xl tracking-widest text-foreground hover:text-white transition-colors">
              rendezvous
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {/* Cities and Experiences hidden for voice-first focus */}
              <NavLink href="/about">About</NavLink>
              {user ? (
                <div className="flex items-center space-x-4">
                  {showAdminButton && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/admin')} className="border-white/20 text-foreground hover:bg-white/10">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={handleMemberHome} className="border-white/20 text-foreground hover:bg-white/10">
                    <User className="h-4 w-4 mr-2" />
                    {memberHomeLabel}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSignOut}
                    onTouchEnd={handleSignOut}
                    disabled={isSigningOut}
                    className="touch-manipulation border-white/20 text-foreground hover:bg-white/10"
                  >
                    {isSigningOut ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Sign Out'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Button variant="outline" size="sm" onClick={() => handleAuth('sign-in')} className="border-white/20 text-foreground hover:bg-white/10">
                    Login
                  </Button>
                  <Button size="sm" onClick={() => handleAuth('sign-up')} className="bg-foreground text-background hover:bg-white">
                    Join Now
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-foreground touch-manipulation p-2 hover:bg-white/10 rounded-full transition-colors"
              onClick={handleMenuToggle}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Optimized for immediate response */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="md:hidden bg-surface-primary/95 backdrop-blur-xl border-b border-white/5"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="container mx-auto px-6 py-8 space-y-6">
                {/* Cities and Experiences hidden for voice-first focus */}
                <MobileNavLink href="/about" onClick={() => setIsOpen(false)}>
                  About
                </MobileNavLink>
                {user ? (
                  <>
                    {showAdminButton && (
                      <Button 
                        variant="outline" 
                        className="w-full touch-manipulation border-white/20 text-foreground hover:bg-white/10 justify-start"
                        onClick={() => {
                          navigate('/admin');
                          setIsOpen(false);
                        }}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Admin
                        {isLoading && <Loader2 className="h-3 w-3 ml-2 animate-spin" />}
                      </Button>
                    )}
                    <Button
                      variant="outline" 
                      className="w-full touch-manipulation border-white/20 text-foreground hover:bg-white/10 justify-start"
                      onClick={handleMemberHome}
                    >
                      <User className="h-4 w-4 mr-2" />
                      {memberHomeLabel}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full touch-manipulation border-white/20 text-foreground hover:bg-white/10 justify-start"
                      onClick={handleSignOut}
                      onTouchEnd={handleSignOut}
                      disabled={isSigningOut}
                    >
                      {isSigningOut ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full touch-manipulation border-white/20 text-foreground hover:bg-white/10"
                      onClick={() => {
                        setIsOpen(false);
                        handleAuth('sign-in');
                      }}
                    >
                      Login
                    </Button>
                    <Button 
                      className="w-full touch-manipulation bg-foreground text-background hover:bg-white"
                      onClick={() => {
                        setIsOpen(false);
                        handleAuth('sign-up');
                      }}
                    >
                      Create Account
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <MemoizedAuthModal />
    </>
  );
}

const NavLink = React.memo(({ href, children }: { href: string; children: React.ReactNode }) => {
  return (
    <Link
      to={href}
      className="text-xs uppercase tracking-[0.2em] transition-colors text-foreground/70 hover:text-foreground font-medium"
    >
      {children}
    </Link>
  );
});

const MobileNavLink = React.memo(
  ({ href, children, onClick }: { href: string; children: React.ReactNode; onClick?: () => void }) => {
    return (
      <Link
        to={href}
        className="block text-2xl font-brand-display transition-colors touch-manipulation text-foreground hover:text-white"
        onClick={onClick}
        style={{ 
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {children}
      </Link>
    );
  }
);
